import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Truck, ArrowLeft, CalendarDays, Loader, ShieldAlert } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { DASHBOARD_ITEM, NAV_GROUPS, VIEWER_PAGES, VIEWER_LOGIN_PAGES, visibleNavItems } from './navConfig';
import { driverPlaces, vehiclePlaces } from '../search/relatedPlaces';

const SEARCH_API = '/.netlify/functions/global-search-sb';

// Open the palette from anywhere (Dashboard box, sidebar, topbar) without
// context plumbing — the mounted palette listens for this window event.
export const openPalette = () => window.dispatchEvent(new Event('hp:open-search'));

// A row's route path (href minus query) — used for viewer whitelisting.
const pathOf = (href) => (href || '').split('?')[0];

export default function CommandPalette() {
  const { currentUser, personnel, vehicles } = useApp();
  const isViewer = currentUser?.role === 'viewer';
  const isAdmin = currentUser?.role === 'admin';
  const myLogin = currentUser?.login || '';
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // drill = a chosen entity whose related-places menu is being shown.
  const [drill, setDrill] = useState(null); // {kind:'driver'|'vehicle', id, label}
  const [serverGroups, setServerGroups] = useState([]);
  const [serverEntity, setServerEntity] = useState(null);
  const [serverLoading, setServerLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const reqIdRef = useRef(0);

  const close = () => { setOpen(false); setQ(''); setDrill(null); setServerGroups([]); setServerEntity(null); };

  // ⌘K / Ctrl-K toggle, open event, Escape.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => { if (o) { setQ(''); setDrill(null); } return !o; });
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('hp:open-search', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('hp:open-search', onOpen); };
  }, []);

  // Focus + body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Strip rows a viewer/non-admin must not see (same rule as the sidebar).
  const allowRow = useMemo(() => (row) => {
    const path = pathOf(row.href);
    const granted = (VIEWER_LOGIN_PAGES[path] || []).includes(myLogin);
    if (row.adminOnly && !isAdmin && !granted) return false;
    if (isViewer && !VIEWER_PAGES.has(path) && !granted) return false;
    return true;
  }, [isAdmin, isViewer, myLogin]);

  const query = q.trim();
  const QU = query.toUpperCase();

  // ── Instant tier: pages, drivers, vehicles from data already in the browser ──
  const instant = useMemo(() => {
    if (drill || query.length < 1) return { sections: [], hrefs: new Set() };
    const needle = query.toLowerCase();
    const sections = [];
    const hrefs = new Set();

    // Page names are matched with punctuation and spacing ignored, so "pre card",
    // "Pre-Card" and "precard" all find the same page. Without this, a hyphen in the
    // label quietly makes the page unfindable by the name people actually type.
    const loose = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const looseNeedle = loose(query);

    const allPages = [DASHBOARD_ITEM, ...NAV_GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, group: g.label })))];
    const pages = visibleNavItems(allPages, { isViewer, isAdmin, login: myLogin })
      .filter((p) => p.label.toLowerCase().includes(needle) || (p.group || '').toLowerCase().includes(needle)
        || (!!looseNeedle && loose(p.label).includes(looseNeedle)))
      .slice(0, 6)
      .map((p) => ({ key: `page:${p.to}`, icon: p.icon, label: p.label, sublabel: p.group || 'Page', href: p.to, newTab: p.newTab }));
    if (pages.length) { sections.push({ group: 'Pages', items: pages }); pages.forEach((p) => hrefs.add(p.href)); }

    const drivers = (personnel || [])
      .filter((p) => (p.loginName || '').toLowerCase().includes(needle)
        || (p.name || '').toLowerCase().includes(needle)
        || (p.companyName || '').toLowerCase().includes(needle))
      .slice(0, 5)
      .map((p) => ({
        key: `driver:${p.loginName}`, icon: User,
        label: `${p.loginName}${p.name ? ' — ' + p.name : ''}`,
        sublabel: [p.status, p.companyName].filter(Boolean).join(' · ') || 'Driver',
        entity: { kind: 'driver', id: p.loginName, label: p.loginName },
      }));
    if (drivers.length) sections.push({ group: 'Drivers', items: drivers });

    const vans = (vehicles || [])
      .filter((v) => (v.rego || '').toLowerCase().includes(needle))
      .slice(0, 5)
      .map((v) => ({
        key: `vehicle:${v.rego}`, icon: Truck,
        label: v.rego,
        sublabel: [v.make, v.model, v.status].filter(Boolean).join(' · ') || 'Vehicle',
        entity: { kind: 'vehicle', id: v.rego, label: v.rego },
      }));
    if (vans.length) sections.push({ group: 'Vehicles', items: vans });

    return { sections, hrefs };
  }, [query, drill, personnel, vehicles, isViewer, isAdmin, myLogin]);

  // ── Server tier: debounced fan-out across the engine's data stores ──
  useEffect(() => {
    if (drill || query.length < 2) { setServerGroups([]); setServerEntity(null); setServerLoading(false); return; }
    setServerLoading(true);
    const id = ++reqIdRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(SEARCH_API, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'search', q: query }),
        });
        const j = await res.json();
        if (reqIdRef.current !== id) return; // stale response
        setServerGroups(j.success ? (j.groups || []) : []);
        setServerEntity(j.success ? (j.entity || null) : null);
      } catch {
        if (reqIdRef.current === id) { setServerGroups([]); setServerEntity(null); }
      } finally {
        if (reqIdRef.current === id) setServerLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, drill]);

  // ── Compose the visible sections ──
  const sections = useMemo(() => {
    if (drill) {
      const places = (drill.kind === 'driver' ? driverPlaces(drill.id) : vehiclePlaces(drill.id)).filter(allowRow);
      const needle = query.toLowerCase();
      const items = places
        .filter((p) => !needle || p.label.toLowerCase().includes(needle))
        .map((p) => ({ key: `place:${p.href}`, icon: p.icon, label: p.label, sublabel: drill.label, href: p.href }));
      return [{ group: `Everything for ${drill.label}`, items: [{ key: 'back', icon: ArrowLeft, label: 'Back to search', sublabel: '', back: true }, ...items] }];
    }
    const out = [...instant.sections];

    // Exact entity match (client or server) → surface its related places inline.
    const exact =
      instant.sections.flatMap((s) => s.items).find((it) => it.entity && it.entity.id.toUpperCase() === QU)?.entity
      || (serverEntity && { kind: serverEntity.kind, id: serverEntity.id, label: serverEntity.id });
    if (exact) {
      const places = (exact.kind === 'driver' ? driverPlaces(exact.id) : vehiclePlaces(exact.id)).filter(allowRow);
      out.push({
        group: `Everything for ${exact.label || exact.id}`,
        items: places.map((p) => ({ key: `place:${p.href}`, icon: p.icon, label: p.label, sublabel: exact.id, href: p.href })),
      });
    }

    // Date query → Log-On Check / Daily Snapshot shortcuts (no server needed).
    if (/^\d{4}-\d{2}-\d{2}$/.test(query)) {
      const items = [
        { key: 'date:login-check', icon: CalendarDays, label: `Log-On Check for ${query}`, sublabel: 'Who logged on that day', href: `/login-check?date=${query}` },
        { key: 'date:snapshot', icon: CalendarDays, label: 'Daily Snapshot', sublabel: query, href: '/daily-snapshot' },
      ].filter(allowRow);
      if (items.length) out.push({ group: 'Dates', items });
    }

    // Instant sections already cover Drivers/Vehicles from AppContext — skip the
    // server's same-named groups so the list doesn't show duplicates.
    const instantGroupNames = new Set(instant.sections.map((s) => s.group));
    for (const g of serverGroups) {
      if (instantGroupNames.has(g.group)) continue;
      const items = (g.items || [])
        .filter((it) => allowRow(it) && !instant.hrefs.has(it.href))
        .map((it, i) => ({ key: `srv:${g.group}:${it.href}:${i}`, icon: iconForType(it.type), label: it.label, sublabel: it.sublabel, href: it.href }));
      if (items.length) out.push({ group: g.group, items });
    }
    return out;
  }, [drill, query, QU, instant, serverGroups, serverEntity, allowRow]);

  const flatRows = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // Keep the active row valid as results change.
  useEffect(() => { setActiveIndex(0); }, [query, drill, sections.length]);

  const runRow = (row) => {
    if (!row) return;
    if (row.back) { setDrill(null); return; }
    if (row.entity) { setDrill(row.entity); setQ(''); return; }
    // A newTab row is a standalone portal outside the admin Layout (see navConfig) —
    // open it the same way the sidebar does rather than navigating the engine into it.
    if (row.href && row.newTab) { close(); window.open(row.href, '_blank', 'noopener'); return; }
    if (row.href) { close(); navigate(row.href); }
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => (i + 1) % Math.max(flatRows.length, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => (i - 1 + Math.max(flatRows.length, 1)) % Math.max(flatRows.length, 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); runRow(flatRows[activeIndex]); }
    else if (e.key === 'Escape') { e.preventDefault(); if (drill) setDrill(null); else close(); }
    else if (e.key === 'Backspace' && !q && drill) { setDrill(null); }
  };

  // Keep the active row scrolled into view.
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, sections]);

  if (!open) return null;

  let rowIndex = -1;
  return (
    <div className="modal-overlay" style={{ alignItems: 'flex-start', paddingTop: '10vh' }} onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal" style={{ maxWidth: 640, width: '92%', padding: 0, overflow: 'hidden' }}>
        <div className="palette-input-row">
          {drill
            ? <span className="palette-drill-chip">{drill.kind === 'driver' ? <User size={12} /> : <Truck size={12} />} {drill.label}</span>
            : <Search size={16} style={{ color: 'var(--hp-dim)', flexShrink: 0 }} />}
          <input
            ref={inputRef}
            className="palette-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={drill ? 'Filter places…' : 'Search rego, login, barcode, page…'}
            spellCheck={false}
            autoComplete="off"
          />
          {serverLoading && <Loader size={14} className="palette-spin" style={{ color: 'var(--hp-dim)', flexShrink: 0 }} />}
        </div>
        <div className="palette-list" ref={listRef}>
          {sections.map((s) => (
            <div key={s.group}>
              <div className="palette-group-label">{s.group}</div>
              {s.items.map((row) => {
                rowIndex += 1;
                const idx = rowIndex;
                const Icon = row.icon || Search;
                return (
                  <button
                    key={row.key}
                    type="button"
                    className={`palette-row${idx === activeIndex ? ' active' : ''}`}
                    data-active={idx === activeIndex ? 'true' : undefined}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => runRow(row)}
                  >
                    <Icon size={15} className="palette-row-icon" />
                    <span className="palette-row-label">{row.label}</span>
                    {row.sublabel ? <span className="palette-row-sub">{row.sublabel}</span> : null}
                  </button>
                );
              })}
            </div>
          ))}
          {query.length >= 1 && flatRows.length === 0 && !serverLoading && (
            <div className="palette-empty">No matches for “{query}”</div>
          )}
          {query.length === 0 && !drill && (
            <div className="palette-empty">Type a rego, driver login, parcel barcode, date (YYYY-MM-DD) or page name.</div>
          )}
        </div>
        <div className="palette-footer">
          <span><kbd className="palette-kbd">↑↓</kbd> navigate</span>
          <span><kbd className="palette-kbd">↵</kbd> open</span>
          <span><kbd className="palette-kbd">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

function iconForType(type) {
  switch (type) {
    case 'driver': return User;
    case 'vehicle': return Truck;
    case 'precard': return ShieldAlert;
    default: return Search;
  }
}
